// On-screen strings of the recreated film, one table per locale. The EN wording follows the real app's English
// locale (groupfaces.front/locales/en.json). Film takes a `locale` prop and provides it; screens read it with useS().
import {createContext, useContext} from 'react';

export type Locale = 'pt' | 'en';
export type GalleryText = {folder: string; name: string; size: string};

// Deterministic thousands separator (no Intl in the renderer).
const group = (sep: string) => (n: number) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, sep);
const NOON = 12;
// "14:02" in 12-hour clock: "2:02 PM"
const to12h = (hm: string) => {
 const [h, m] = hm.split(':').map(Number);
 return `${h % NOON || NOON}:${String(m).padStart(2, '0')} ${h < NOON ? 'AM' : 'PM'}`;
};

const PT = {
 num: group('.'),
 stamp: (hm: string) => `29/10 · ${hm}`,
 nav: ['Painel', 'Projetos', 'Tokens', 'Subscrição', 'Estatísticas', 'Ajuda'],
 studio: 'Estúdio Luz Norte',
 dashboard: 'Painel',
 dashCards: ['PROJETOS ATIVOS', 'FOTOS PROCESSADAS', 'LINKS ABERTOS'],
 recent: 'Projetos recentes',
 recentList: ['Formatura Medicina Turma 2026', 'Formatura Direito 2026', 'Formatura Engenharia 2025'],
 done: 'Concluído',
 processing: 'Processando',
 steps: ['Info do Projeto', 'Tokens', 'Targets', 'Fotos do Evento', 'Confirmação'],
 targetsTitle: 'Referências faciais (Targets)',
 targetsSub: 'Envie fotos de cada pessoa que deseja identificar no evento.',
 oneFace: '1 rosto',
 namePh: 'Nome',
 emailPh: 'email@exemplo.com',
 addMore: 'Adicionar mais fotos',
 eventTitle: 'Fotos do evento',
 eventSub: 'Organize as fotos do evento em galerias. Cada pasta se tornará uma galeria.',
 galleriesN: (n: number) => `${n} galerias ·`,
 photos: 'fotos',
 files: 'arquivos',
 galleries: {
  graduation: {folder: 'colacao-de-grau/', name: 'Colação de Grau', size: '1,9 GB'},
  ball: {folder: 'baile-de-formatura/', name: 'Baile de Formatura', size: '2,6 GB'},
  campus: {folder: 'ensaio-no-campus/', name: 'Ensaio no Campus', size: '1,8 GB'},
  studio: {folder: 'ensaio-em-estudio/', name: 'Ensaio em Estúdio', size: '1,3 GB'},
 } as Record<string, GalleryText>,
 back: 'Projetos',
 project: 'Formatura Medicina Turma 2026',
 tabs: ['Visão geral', 'Galerias', 'Clientes', 'Entrega'],
 deliveryTab: 3,
 recognitionDone: 'Reconhecimento concluído',
 processingPhotos: 'Processando fotos',
 metrics: ['Total de fotos', 'Galerias', 'Clientes identificados'],
 sendTitle: 'Entrega por e-mail',
 sendSub: 'Cada cliente recebe o link da própria galeria.',
 selected: (clients: number, photos: string) => `${clients} clientes selecionados · ${photos} fotos`,
 sendNote: 'O e-mail sai direto da plataforma, com o link individual de cada galeria.',
 send: 'Enviar galerias',
 sent: 'Galerias enviadas',
 states: ['Enviado', 'Aberto', 'Baixado'],
 line1: ['Milhares de fotos. ', 'Um rosto de cada vez.'],
 line2: ['A formatura inteira, ', 'organizada por aluno.'],
};
export type Strings = typeof PT;

const EN: Strings = {
 num: group(','),
 stamp: (hm: string) => `10/29 · ${to12h(hm)}`,
 nav: ['Dashboard', 'Projects', 'Tokens', 'Subscription', 'Analytics', 'Help'],
 studio: 'Northlight Studio',
 dashboard: 'Dashboard',
 dashCards: ['ACTIVE PROJECTS', 'PHOTOS PROCESSED', 'LINKS OPENED'],
 recent: 'Recent projects',
 recentList: ['Medical School Class of 2026', 'Law School Class of 2026', 'Engineering Class of 2025'],
 done: 'Done',
 processing: 'Processing',
 steps: ['Project Info', 'Tokens', 'Targets', 'Event Photos', 'Confirmation'],
 targetsTitle: 'Facial references (Targets)',
 targetsSub: 'Upload photos of each person you want to identify at the event.',
 oneFace: '1 face',
 namePh: 'Name',
 emailPh: 'email@example.com',
 addMore: 'Add more photos',
 eventTitle: 'Event photos',
 eventSub: 'Organize event photos into galleries. Each folder will become a gallery.',
 galleriesN: (n: number) => `${n} ${n === 1 ? 'gallery' : 'galleries'} ·`,
 photos: 'photos',
 files: 'files',
 galleries: {
  graduation: {folder: 'graduation-ceremony/', name: 'Graduation Ceremony', size: '1.9 GB'},
  ball: {folder: 'graduation-ball/', name: 'Graduation Ball', size: '2.6 GB'},
  campus: {folder: 'campus-shoot/', name: 'Campus Shoot', size: '1.8 GB'},
  studio: {folder: 'studio-shoot/', name: 'Studio Shoot', size: '1.3 GB'},
 },
 back: 'Projects',
 project: 'Medical School Class of 2026',
 tabs: ['Overview', 'Galleries', 'Clients', 'Unidentified', 'Usage'],
 deliveryTab: 2,   // the app shows link activity on the Clients tab
 recognitionDone: 'Recognition complete',
 processingPhotos: 'Processing photos',
 metrics: ['Total photos', 'Total galleries', 'Identified clients'],
 sendTitle: 'E-mail delivery',
 sendSub: 'Each client gets the link to their own gallery.',
 selected: (clients: number, photos: string) => `${clients} clients selected · ${photos} photos`,
 sendNote: 'The e-mail goes out straight from the platform, with each gallery\'s own link.',
 send: 'Send galleries',
 sent: 'Galleries sent',
 states: ['Sent', 'Opened', 'Downloaded'],
 line1: ['Thousands of photos. ', 'One face at a time.'],
 line2: ['The whole graduation, ', 'organized by student.'],
};

export const STRINGS: Record<Locale, Strings> = {pt: PT, en: EN};
export const LocaleContext = createContext<Locale>('pt');
export const useS = (): Strings => STRINGS[useContext(LocaleContext)];

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
